import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    // A slide image that has not been given its URL yet (product cards defer
    // the URL of every slide after the first) has no currentSrc to compare.
    if (!this.featuredMediaUrl || !image.currentSrc) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * How far the pointer may travel between press and release and still count as a tap
 * rather than a scroll or a drag, in CSS pixels. Matched to the slack the slideshow's
 * own flick detection already allows (`distanceTravelled < 10` in slideshow.js), so a
 * gesture the carousel treats as movement is one the card treats as movement too.
 */
const TAP_SLOP_PX = 10;

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  /**
   * Shared observer that kicks off the next slide's image load just before a
   * carousel-mode card scrolls into view. Carousel slides are lazy-loaded and
   * only start downloading once scrolled into the card's scrollport — after the
   * first arrow press or swipe is already revealing them — so the second image
   * is fetched ahead of time, matching the eagerly-stacked second image of the
   * hover-crossfade mode and the hover overlay's own copy of it. Held back until
   * the page has seen an interaction (#activation below).
   */
  static #nextSlidePreloadObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting || !(entry.target instanceof ProductCard)) continue;
        ProductCard.#nextSlidePreloadObserver.unobserve(entry.target);
        entry.target.#preloadNextPreviewImage(true);
      }
    },
    { rootMargin: '200px 0px' }
  );

  /**
   * Whether anyone has interacted with the page yet - a press, a key or a touch
   * anywhere on it. The viewport-stage preload (#preloadNextPreviewImage) waits
   * for this, because it must not run for a crawler. Slides after the first
   * carry their image URL in data-src until the theme moves it into src (see
   * the slides loop in card-gallery.liquid), so that the featured image is the
   * one image URL a crawler can read per card. A headless render has a tall
   * viewport and every card "nears" it, so an unguarded viewport stage would put
   * the second image back into the DOM during Google's render and undo that.
   * Googlebot dispatches no input events; a shopper produces one before they can
   * press an arrow or complete a swipe.
   *
   * `navigator.userActivation` covers an activation that landed before this
   * module ran. The listeners cover browsers without it, and count the touch
   * that starts a scroll (touchstart, rather than the activation-only touchend),
   * so a phone shopper is active from their first gesture. Cards whose viewport
   * stage was skipped wait in #awaitingActivation and run on the first
   * activation, so the two-stage preload is back for every card from the first
   * interaction onwards. One page-lifetime controller: the listeners remove
   * themselves through its signal once they have fired.
   * See docs/maintenance/upstream-divergences.md #51.
   */
  static #activation = new AbortController();

  /**
   * Cards whose viewport-stage preload is waiting on the first activation, with
   * the `speculative` flag each asked for.
   * @type {Map<ProductCard, boolean>}
   */
  static #awaitingActivation = new Map();

  static #pageActivated() {
    return ProductCard.#activation.signal.aborted || navigator.userActivation?.hasBeenActive === true;
  }

  static #handleFirstActivation = () => {
    ProductCard.#activation.abort();
    const pending = [...ProductCard.#awaitingActivation];
    ProductCard.#awaitingActivation.clear();
    for (const [card, speculative] of pending) card.#preloadNextPreviewImage(speculative);
  };

  static {
    for (const type of ['pointerdown', 'keydown', 'touchstart']) {
      document.addEventListener(type, ProductCard.#handleFirstActivation, {
        capture: true,
        passive: true,
        signal: ProductCard.#activation.signal,
      });
    }
  }

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Capture phase, so this runs before both navigateToProduct (bubble, on this same
    // element) and the card anchor's own default navigation — the two ways a stray
    // click becomes a page load.
    this.addEventListener('pointerdown', this.#recordPressOrigin, true);
    this.addEventListener('pointercancel', this.#clearPressOrigin, true);
    this.addEventListener('click', this.#suppressDragClick, true);

    // Media pause events don't bubble, so listen in the capture phase
    this.addEventListener('pause', this.#resumeAutoplayVideo, true);
    this.#observeAutoplayVideoViewport();

    // Preload the next image on the slideshow to avoid white flashes when the
    // next slide is revealed
    this.#preloadTimeout = setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      } else if (this.refs.slideshow && this.refs.slideshow.getAttribute('disabled') !== 'true') {
        // Attribute check rather than the .disabled getter — the slideshow custom
        // element may not be upgraded yet when this runs
        ProductCard.#nextSlidePreloadObserver.observe(this);
      }
    });

    this.addEventListener('pointerenter', this.#preloadCarouselImages);
    this.addEventListener('focusin', this.#preloadCarouselImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('pointerdown', this.#recordPressOrigin, true);
    this.removeEventListener('pointercancel', this.#clearPressOrigin, true);
    this.removeEventListener('click', this.#suppressDragClick, true);
    this.removeEventListener('pause', this.#resumeAutoplayVideo, true);
    this.removeEventListener('pointerenter', this.#preloadCarouselImages);
    this.removeEventListener('focusin', this.#preloadCarouselImages);
    mediaQueryLarge.removeEventListener('change', this.#handleQuickAdd);
    // Left pending, this would observe() a detached card on a page-lifetime static
    // observer, which then holds the whole card subtree for good
    clearTimeout(this.#preloadTimeout);
    ProductCard.#nextSlidePreloadObserver.unobserve(this);
    ProductCard.#awaitingActivation.delete(this);
    this.#cardViewportObserver?.disconnect();
  }

  /**
   * The pending preload scheduled by connectedCallback.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #preloadTimeout;

  /**
   * Eagerly loads the rest of a carousel-mode card's slide images the first time
   * the shopper shows intent (hover, touch, or focus), so arrow clicks and swipes
   * land on an already-loaded slide instead of flashing the card background while
   * a lazy image downloads. One-shot per card; skipped for hover-swap cards, which
   * only ever reach the second image.
   *
   * Keyed on carousel mode rather than the slideshow's `disabled` attribute: a card
   * nested in a resource-list carousel is disabled — the parent owns swipe and drag
   * — but it still pages through its images with the arrows, so it needs this too.
   *
   * This is also where slides after the first receive their image URL: they render
   * with it in data-src (card-gallery.liquid), and #loadSlideImage moves it across
   * before the load starts.
   */
  #preloadCarouselImages = () => {
    const { slideshow } = this.refs;
    if (!slideshow || !this.querySelector('.card-gallery--carousel')) return;

    // A nested card is paged with its arrows, which only exist on desktop; below that
    // the row owns the swipe and the card never leaves its first image, so there is
    // nothing to preload for — and a tap shouldn't pull four images over mobile data.
    if (slideshow.isNested && !isDesktopBreakpoint()) return;

    this.removeEventListener('pointerenter', this.#preloadCarouselImages);
    this.removeEventListener('focusin', this.#preloadCarouselImages);

    // Only the next slide is imminent, so it loads at default priority. The rest are
    // speculative and drop to low, or a pointer sweeping a grid would have every card
    // it crosses fetching four full-size images against ones still painting.
    let next = true;

    for (const image of slideshow.querySelectorAll('slideshow-slide:not([hidden]) img[loading="lazy"]')) {
      if (!(image instanceof HTMLImageElement)) continue;
      ProductCard.#loadSlideImage(image, next ? undefined : 'low');
      next = false;
    }
  };

  /**
   * Starts a slide image loading, first giving it its URL if the slide deferred
   * one. Slides after the first render with data-src / data-srcset in place of
   * src / srcset (card-gallery.liquid, for the crawler); every path that reveals
   * or preloads a slide comes through here, so a deferred image never has to
   * load itself.
   *
   * The order inside is deliberate. The slide's `sizes` begins with `auto`, which
   * Chrome honours only on a lazy image - on an eager one it discards the whole
   * list and falls back to 100vw (divergences #36). Chrome selects the source in
   * a microtask after the attributes change (or at once, when the URL is already
   * in memory cache - the overlay's warm copy), so `loading` is removed in a
   * microtask queued behind that: the candidate is chosen while the image is
   * still lazy, from its laid-out width, and only then does the deferred load go
   * out. That is the pick the slide made when it carried src from the server,
   * and so the URL the hover overlay pinned to reuse (bd1b630).
   *
   * The `offsetWidth` read is a forced layout, on purpose. Chrome resolves
   * `sizes="auto"` from the image's layout box, and an image with no box yet
   * falls back to 100vw - which at desktop widths is the 3840 rung. A card
   * nested in a recommendations carousel is preloaded from its connect
   * timeout, before the section it was injected into has been laid out, and
   * that pick was measured at 3840 on the live theme (5 of 8 cards, the rest
   * rescued by the cache-aware pick) against 832 once a box exists. Cards in
   * the grid already have one, so this costs nothing there.
   * @param {HTMLImageElement} image - The slide image.
   * @param {'low'} [fetchPriority] - Lowered for loads with no interaction intent yet.
   */
  static #loadSlideImage(image, fetchPriority) {
    const { src, srcset } = image.dataset;

    if (src || srcset) void image.offsetWidth;

    if (srcset) {
      image.srcset = srcset;
      delete image.dataset.srcset;
    }
    if (src) {
      image.src = src;
      delete image.dataset.src;
    }
    if (fetchPriority) image.fetchPriority = fetchPriority;

    if (src || srcset) {
      queueMicrotask(() => image.removeAttribute('loading'));
    } else {
      image.removeAttribute('loading');
    }
  }

  /** @type {IntersectionObserver | undefined} */
  #cardViewportObserver;

  /** Whether the card is currently near the viewport. Assumed true until observed. */
  #cardInViewport = true;

  /**
   * Pauses the card's autoplaying video while the card is scrolled out of view and
   * resumes it on return, so an off-screen card video stops decoding instead of being
   * force-resumed by #resumeAutoplayVideo. No-op for cards without an autoplay video.
   */
  #observeAutoplayVideoViewport() {
    if (!this.querySelector('deferred-media[autoplay] video')) return;

    this.#cardViewportObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#cardInViewport = entry.isIntersecting;
          const video = this.querySelector('deferred-media[autoplay] video');
          if (!(video instanceof HTMLVideoElement)) continue;
          if (entry.isIntersecting) {
            if (!document.hidden) video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      },
      { rootMargin: '200px 0px' }
    );
    this.#cardViewportObserver.observe(this);
  }

  /**
   * Resumes the card's autoplaying video when something pauses it, e.g. the browser
   * pausing the offscreen video after the hover image swap scrolls it away, or a
   * closing dialog pausing all deferred media.
   * @param {Event} event - The pause event.
   */
  #resumeAutoplayVideo = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    if (!video.closest('deferred-media[autoplay]') || document.hidden) return;
    // Don't fight the browser pausing an off-screen card video — only the in-view card's
    // video is force-resumed (e.g. after a hover swap or a closing dialog).
    if (!this.#cardInViewport) return;

    requestAnimationFrame(() => video.play().catch(() => {}));
  };

  /**
   * Starts loading the image in the slide after the current one.
   * @param {boolean} [speculative] - Fetch at low priority — for preloads with no
   *   interaction intent yet, so they don't compete with visible images.
   */
  #preloadNextPreviewImage(speculative = false) {
    // Not before the shopper has touched the page - see #activation. The card
    // is queued and this runs again on the first interaction.
    if (!ProductCard.#pageActivated()) {
      ProductCard.#awaitingActivation.set(this, speculative);
      return;
    }

    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    const image = currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]');
    if (!(image instanceof HTMLImageElement)) return;
    ProductCard.#loadSlideImage(image, speculative ? 'low' : undefined);
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    this.#variantSelected = true;

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;
    this.#previousSlideId = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * The slide-id counterpart of #previousSlideIndex. Preferred when restoring:
   * revealing a hidden variant slide shifts every index after it, so a remembered
   * index can resolve to a different slide than the one the shopper chose.
   * @type {string | null}
   */
  #previousSlideId = null;

  /** Whether the shopper has actively selected a variant (as opposed to the preselected default). */
  #variantSelected = false;

  /**
   * Whether the hover preview is painted over the card's first slide right now.
   * The overlay's computed opacity is the CSS gate's live verdict — fine
   * pointer, card hovered, resting on the first slide, not yet
   * shopper-navigated, not mid-drag or mid-transition — so callers key off what
   * is actually visible rather than a re-derivation that could drift from the
   * stylesheet. Past half opacity the shopper predominantly sees the previewed
   * image; below it the slide's own.
   *
   * Only answers for the resting preview, so a ride in progress has to be
   * retired before asking — the ride is itself an opacity override, and left
   * standing it reads back as "the preview is showing" and re-arms every press.
   * @returns {boolean} Whether the preview is showing.
   */
  #hoverOverlayShowing() {
    const overlay = this.querySelector('.card-gallery__hover-overlay');

    return overlay != null && parseFloat(getComputedStyle(overlay).opacity) >= 0.5;
  }

  /**
   * Handles the slideshow select event. A user-initiated selection is recorded
   * as the card's chosen slide so a swatch preview's reset returns to it
   * instead of the initial image. With the hover preview a stateless CSS
   * overlay (see card-gallery.liquid) the scroller only ever moves through
   * real navigation, so any state the browser captures or restores here is a
   * legitimate resting state — no trigger-based gating is needed.
   *
   * Hand navigation — arrows or a drag, `trigger` 'select'/'drag' — also marks
   * the card shopper-navigated, retiring the hover preview for the rest of the
   * page life (the CSS overlay gate in card-gallery.liquid; the pageshow
   * listener below re-arms it when the shopper returns): once the shopper
   * takes control of the carousel, its image is theirs, even back on the first
   * slide. A UI-mode flag, not scroll state — nothing here touches or corrects
   * the scroller. `trigger: 'scroll'` deliberately doesn't arm it: on
   * fine-pointer devices the scroller is closed to gesture scrolling (overflow
   * gate), so a scroll-attributed select there is browser-driven — history
   * restoration replaying a past visit, not navigation on this page — and on
   * touch it's a real swipe but there is no hover to retire.
   *
   * Hand navigation also hands the preview to the slide it is painted on for
   * the length of the move (`data-overlay-riding`, held opaque by the CSS rule
   * in card-gallery.liquid), so the shopper watches the previewed image leave
   * the frame rather than the first slide's own image flashing back into a
   * frame it is about to leave. A press runs a whole frame ahead of the
   * scroller's first pixel, and both of the things that retire the preview land
   * inside that gap — `data-shopper-navigated` below, and `[transitioning]` from
   * the scroller's first scroll event a frame later — so the overlay has to be
   * read here, before the stamp, while it still answers for what the shopper was
   * looking at when they pressed.
   *
   * The ride is superseded rather than timed out: every selection clears it and
   * every route back to the first slide is a selection, made in the same task
   * that moves the scroller, so that slide never paints wearing a stale preview.
   * In between it sits opaque on a slide that is off-frame. Neither of the
   * states that look like end signals can serve as one — `[transitioning]`
   * arrives a frame too late to cover the flash, and the slide's `aria-hidden`
   * flips about half-way out (the visibility observer's 0.7 threshold), which
   * would uncover the rest of the exit.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    const { cardGallery } = this.refs;

    // The slide being shown may still hold its URL in data-src - a selection made
    // with no pointer contact or focus on the card, such as the setup fallback
    // adopting a restored scroll offset, or a swatch revealing its variant image.
    // Give it one in the same task as the move, so the slide never paints empty.
    const deferred = event.detail.slide?.querySelector('img[data-src]');
    if (deferred instanceof HTMLImageElement) ProductCard.#loadSlideImage(deferred);

    // Retire the outgoing ride before asking about this one, in that order: the
    // hold is an opacity override on the same element the question is asked of.
    cardGallery?.removeAttribute('data-overlay-riding');

    if (event.detail.userInitiated && this.#hoverOverlayShowing()) {
      cardGallery?.setAttribute('data-overlay-riding', '');
    }

    if (!event.detail.userInitiated) return;

    this.#previousSlideIndex = event.detail.index;
    this.#previousSlideId = event.detail.id ?? null;

    if (event.detail.trigger !== 'scroll') {
      this.refs.cardGallery?.setAttribute('data-shopper-navigated', '');
    }
  };

  /**
   * Restores the slide the shopper chose themselves, if they have chosen one.
   * @returns {boolean} Whether there was one to restore.
   */
  #restoreChosenSlide() {
    const { slideshow } = this.refs;

    if (!slideshow || this.#previousSlideIndex == null) return false;

    if (this.#previousSlideId != null) {
      slideshow.select({ id: this.#previousSlideId }, undefined, { animate: false });
    } else {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    }

    return true;
  }

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Whether the shopper is looking at the hover preview: the card is resting on
   * its first slide with the overlay painted over it. The one state in which
   * what they see is a step ahead of where the scroller is — second image,
   * pagination position 2, `current` still 0 — and so the state both arrows
   * have to correct for. Derived at press time from live conditions, storing
   * nothing; the only actor mutating the scroller is the deliberate press.
   *
   * The cheap index read comes first on purpose: `#hoverOverlayShowing()` calls
   * `getComputedStyle`, and every press after the first short-circuits before
   * paying for it.
   *
   * `current === 0` is also what stops a standing ride from re-arming a
   * correction. `[data-overlay-riding]:hover` holds the overlay opaque after a
   * press, so it reads back as "showing" on the next press even though the card
   * is already shopper-navigated — but only ever on a card that has moved off
   * slide 0, because a ride is armed from the resting gate alone (cleared
   * before it is read, in `#handleSlideshowSelect`) and that gate cannot match
   * a shopper-navigated card. Below half opacity the shopper predominantly sees
   * the slide's own image, where both arrows are already honest.
   * @returns {boolean} Whether the preview is what the shopper is looking at.
   */
  #restingUnderPreview() {
    const { slideshow } = this.refs;

    return slideshow != null && slideshow.current === 0 && this.#hoverOverlayShowing();
  }

  /**
   * Advances the card carousel from its next arrow, relative to what the
   * shopper sees. Wired via `next_on_click` in card-gallery.liquid when the
   * hover overlay is on: while the overlay is showing the second image over a
   * card resting on its first slide, a plain next would scroll to that
   * identical second image and the press would read as "nothing happened" — so
   * skip to the third slide instead. Keyboard activation never hovers, so the
   * overlay is hidden and keyboard users are never skipped past the second
   * slide.
   * @param {Event} event - The click event from the next arrow.
   */
  nextImage(event) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // The skip needs a third slide to land on — with two, the infinite carousel
    // would wrap back to the overlaid first slide and the press would read as
    // "nothing happened" forever. A plain next to the second slide at least
    // moves the resting state and the pagination line forward.
    if (this.#restingUnderPreview() && (slideshow.slides?.length ?? 0) > 2) {
      slideshow.select(2, event);
    } else {
      slideshow.next(event);
    }
  }

  /**
   * Steps the card carousel back from its previous arrow, relative to what the
   * shopper sees. Wired via `previous_on_click` alongside `nextImage`, but the
   * correction is not the mirror of that one: next from the preview is a real
   * move (0 -> 2), while previous from the preview is not a move at all. The
   * image the shopper is asking to go back to is the first slide's own, and the
   * scroller is already resting on it — so the action is to retire the preview
   * and stay put.
   *
   * Left stock, `previous()` steps from where the scroller is rather than from
   * what the shopper sees, taking `previousIndex` from 0, which on an infinite
   * carousel wraps to the last slide: previous from apparent position 2 landed
   * on the last image, stepping over image 1 entirely. The asymmetry dated from
   * the overlay rearchitecture, which routed only the next arrow through the
   * card (fixed 2026-08-19; see divergences #22).
   *
   * Needs no slide-count guard, unlike the next skip. That skip can wrap onto
   * the overlaid slide and read as "nothing happened"; a dismissal cannot — it
   * always uncovers the image underneath, on a two-slide card as much as a
   * ten-slide one.
   * @param {Event} event - The click event from the previous arrow.
   */
  previousImage(event) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    if (this.#restingUnderPreview()) {
      this.#dismissHoverPreview(event);
      return;
    }

    slideshow.previous(event);
  }

  /**
   * Retires the hover preview without moving the scroller, uncovering the slide
   * it is painted on.
   *
   * Deliberately not routed through `select(0)`. The scroller is already
   * resting on slide 0, so `select()` returns at its same-slide guard — before
   * it dispatches, and before its own `preventDefault()`, which would leave the
   * press to bubble to the anchor wrapping the gallery and navigate to the
   * product page. Hence the explicit `preventDefault()`: the arrows are
   * `<button>`s inside that anchor, and every other arrow path inherits this
   * from `select()`.
   *
   * One attribute does the whole job. `data-shopper-navigated` is the same
   * stamp hand navigation leaves, and setting it here closes both halves of the
   * CSS gate in card-gallery.liquid in a single style recalc: the overlay loses
   * its opacity (instantly — the same stamp switches its transition off) and
   * the scoped `--line-current: 1 !important` releases, handing the pagination
   * line back to the component's own inline value at position 1. There is no
   * frame in which one has changed and the other has not.
   *
   * No `data-overlay-riding`: a ride exists to carry the preview off with a
   * slide that is leaving, and nothing is leaving. Stamped here it would hold
   * the overlay opaque until the next selection — which is to say the press
   * would visibly do nothing.
   *
   * The chosen-slide memory is left alone for the same reason: it records where
   * the shopper moved the card to, and they have moved it nowhere. An empty
   * memory already resolves to this slide for the swatch-preview reset that
   * reads it, so writing 0 would change nothing except to start overriding a
   * selected variant's image — which this fix has no cause to do.
   * @param {Event} [event] - The click event from the previous arrow.
   */
  #dismissHoverPreview(event) {
    event?.preventDefault();
    this.refs.cardGallery?.setAttribute('data-shopper-navigated', '');
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // Reached from a swatch's pointerleave. A slide the shopper navigated to
    // outranks the variant image: return to it rather than resetting the card
    // out from under them.
    if (this.#restoreChosenSlide()) return;

    // Until the shopper actively picks a variant, an autoplaying video in the first
    // slide is the card's resting state — return to it instead of the preselected
    // variant's image
    if (!this.#variantSelected) {
      const initialSlide = slideshow.initialSlide;
      const initialSlideId = initialSlide?.getAttribute('slide-id');
      if (initialSlideId && initialSlide.querySelector('deferred-media[autoplay] video')) {
        slideshow.select({ id: initialSlideId }, undefined, { animate: false });
        return;
      }
    }

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Viewport coordinates of the press that may become the current click, or null when
   * there is nothing to compare against — a keyboard activation, or a press that began
   * outside this card (the browser only fires a click on the nearest common ancestor of
   * press and release, so such a gesture never reaches this handler anyway).
   * @type {{ x: number, y: number } | null}
   */
  #pressOrigin = null;

  /** @param {PointerEvent} event */
  #recordPressOrigin = (event) => {
    this.#pressOrigin = { x: event.clientX, y: event.clientY };
  };

  #clearPressOrigin = () => {
    this.#pressOrigin = null;
  };

  /**
   * Drops the click a gesture leaves behind when that gesture was a scroll or a drag
   * rather than a tap.
   *
   * The whole card is a navigation surface: `navigateToProduct` fires on any click on
   * this element and sends the shopper to the PDP, and the card anchor navigates
   * natively on top of that. Neither checks how far the pointer travelled, so any click
   * the browser decides to emit is a page load — including the one WebKit emits at
   * `touchend` for a touch sequence that never scrolled, which is exactly what the
   * slideshow's edge lock manufactures when it cancels a swipe at the first or last
   * slide (`#handleTouchMove` in slideshow.js).
   *
   * Upstream does try to cover the mouse case — `#handleMouseDown` registers
   * `document`-level `click` -> `preventDefault` for the duration of a drag — but that
   * listener is tied to the drag's `AbortController`, and `onPointerUp` aborts it as its
   * first statement. `pointerup` precedes `click`, so the listener is always removed a
   * moment before it would have fired. A 120px mouse drag across a card navigates to the
   * PDP on the live theme. This guard replaces that mechanism rather than repairing it:
   * one distance test at the card covers mouse, touch and pen, and covers the parts of
   * the card that are not the slideshow scroller.
   *
   * `stopPropagation` as well as `preventDefault`, because `navigateToProduct` assigns
   * `window.location.href` directly and never consults `defaultPrevented`.
   *
   * @param {MouseEvent} event
   */
  #suppressDragClick = (event) => {
    const origin = this.#pressOrigin;
    this.#pressOrigin = null;

    // `detail === 0` is a keyboard activation (Enter/Space on the anchor), which has no
    // pointer travel to measure and must always be allowed through.
    if (!origin || event.detail === 0) return;

    const travelled = Math.hypot(event.clientX - origin.x, event.clientY - origin.y);
    if (travelled <= TAP_SLOP_PX) return;

    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

// "Retired until the shopper leaves the page" ends at leaving, however they come
// back: a back/forward-cache restore returns the DOM exactly as it was left,
// shopper-navigated flags included, so clear them and let hover preview again
// (classic navigations rebuild the DOM without the flag and need nothing). The
// ride flag goes with it: it only means "the preview is still handed to a slide
// that is off-frame", which a re-armed preview would contradict. Both are plain
// UI state — they are the only things that reset on return; scroll positions are
// the browser's to restore and are never touched or corrected
// (docs/maintenance/upstream-divergences.md #22). One page-lifetime listener
// rather than one per card: it needs no teardown and covers every card at once.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  for (const gallery of document.querySelectorAll('.card-gallery[data-shopper-navigated], .card-gallery[data-overlay-riding]')) {
    gallery.removeAttribute('data-shopper-navigated');
    gallery.removeAttribute('data-overlay-riding');
  }
});

/**
 * Diagnostic trace for card-carousel interaction bugs (sticky hover-preview image
 * — see docs/maintenance/upstream-divergences.md #22). Records every card-gallery
 * pointer boundary event, press, wheel, slideshow selection and pageshow into an
 * in-memory ring buffer so a shopper-reported repro can be captured from the
 * affected page instead of reconstructed.
 *
 * Off for shoppers: enabled automatically on theme dev (localhost), or anywhere
 * by running `sessionStorage.setItem('apdebug', '1')` in DevTools and reloading.
 * Read with `__apCardTrace()` in DevTools; export with
 * `copy(JSON.stringify(__apCardTrace()))`.
 */
(() => {
  let enabled = false;
  try {
    enabled = ['127.0.0.1', 'localhost'].includes(location.hostname) || sessionStorage.getItem('apdebug') === '1';
  } catch {
    // sessionStorage unavailable — leave the trace off
  }
  if (!enabled) return;

  /** @type {Record<string, unknown>[]} */
  const events = [];
  const t0 = performance.now();

  /** @param {Record<string, unknown>} entry */
  const push = (entry) => {
    events.push({ t: Math.round(performance.now() - t0), ...entry });
    if (events.length > 400) events.splice(0, 100);
  };

  /** @param {EventTarget | null} node */
  const cardId = (node) =>
    node instanceof Element
      ? node.closest('product-card')?.querySelector('[data-product-id]')?.getAttribute('data-product-id')
      : undefined;

  document.addEventListener(
    SlideshowSelectEvent.eventName,
    (event) => {
      const card = cardId(event.target);
      if (!card) return;
      const { slide, ...detail } = /** @type {SlideshowSelectEvent} */ (event).detail;
      push({ e: 'select', card, ...detail });
    },
    { capture: true }
  );

  for (const type of ['pointerenter', 'pointerleave']) {
    document.addEventListener(
      type,
      (event) => {
        if (!(event.target instanceof Element) || !event.target.matches('.card-gallery')) return;
        push({ e: type, card: cardId(event.target), pointerType: /** @type {PointerEvent} */ (event).pointerType });
      },
      { capture: true, passive: true }
    );
  }

  for (const type of ['pointerdown', 'pointerup', 'wheel']) {
    document.addEventListener(
      type,
      (event) => {
        const card = cardId(event.target);
        if (!card) return;
        const { button, metaKey, ctrlKey, pointerType } = /** @type {PointerEvent & WheelEvent} */ (event);
        push({ e: type, card, button, meta: metaKey || ctrlKey, pointerType });
      },
      { capture: true, passive: true }
    );
  }

  window.addEventListener('pageshow', (event) => push({ e: 'pageshow', persisted: event.persisted }));

  // Early scroll-and-paint timeline: history scroll restoration lands as scroll
  // events on the card scrollers (scroll doesn't bubble, but capture sees it),
  // and lining those up against the paint entries shows whether a stale offset
  // reached a painted frame or was corrected pre-paint.
  document.addEventListener(
    'scroll',
    (event) => {
      const scroller = event.target;
      if (!(scroller instanceof Element) || scroller.tagName !== 'SLIDESHOW-SLIDES') return;
      const card = cardId(scroller);
      if (!card) return;
      push({ e: 'scroll', card, left: Math.round(scroller.scrollLeft) });
    },
    { capture: true, passive: true }
  );

  document.addEventListener('DOMContentLoaded', () => push({ e: 'DOMContentLoaded' }), { once: true });
  window.addEventListener('load', () => push({ e: 'load' }), { once: true });

  try {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) push({ e: `paint:${entry.name}`, t: Math.round(entry.startTime - t0) });
    }).observe({ type: 'paint', buffered: true });
  } catch {
    // paint timing unsupported — timeline still usable without it
  }

  push({
    e: 'init',
    offsets: [...document.querySelectorAll('product-card slideshow-slides')].map((scroller) => ({
      card: cardId(scroller),
      left: Math.round(scroller.scrollLeft),
    })),
  });

  /** @type {any} */ (window).__apCardTrace = () => ({
    startedAt: new Date(Date.now() - (performance.now() - t0)).toISOString(),
    events: [...events],
    cards: [...document.querySelectorAll('product-card')].map((card) => ({
      card: card.querySelector('[data-product-id]')?.getAttribute('data-product-id'),
      current: /** @type {any} */ (card.querySelector('slideshow-component'))?.current,
      scrollLeft: card.querySelector('slideshow-slides')?.scrollLeft,
    })),
  });
})();

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
